import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';

import Alert from 'reactstrap/lib/Alert';

import { receiveProducts, receiveTickets } from '@ttstr/actions';
import { Container, LoadingSpinner, Masonry, useIntl, Video } from '@ttstr/components';
import { AppState } from '@ttstr/reducers';
import { useActions, useShallowEqualSelector } from '@ttstr/utils';
import { Article } from '@ttstr/api';
import FilteredMasonry from './FilteredMasonry';

// wird nicht mehr gebraucht: Header soll ganz raus
// import MerchstandImages from './assets/images/MerchstandImages';

interface RouteParams {
  readonly id: string;
}

type Props = {
  /**
   * productFilter can be used if you want a Route to a dedicated page for special merch/products.
   *
   * Example:
   * You want a page only with merch that has the tag id 1234.
   * The function for productFilter would then be
   *  p => p.tag_ids.includes(1234)
   *
   * Then create a route in App.tsx with
   * <Route exact path="/specialmerch" render={()=> <ProductListing productFilter={p => p.tag_ids.includes(1234)} />} />
   */
  productFilter?: (p: Article) => any;
};

const Home: React.FC<Props> = ({ productFilter = (p) => p }) => {
  const { t } = useTranslation();
  const params = useParams<RouteParams>();
  const { products, loadingProducts, tickets, loadingTickets, loading } = useShallowEqualSelector(mapStateToProps);
  const { receiveProducts, receiveTickets } = useActions(mapDispatchToProps);
  const { language } = useIntl();

  if (!productFilter && params?.id) {
    productFilter = (p) => p.category_ids.includes(Number(params.id));
  }

  React.useEffect(() => {
    receiveProducts();
    receiveTickets();
  }, [language]);

  return (
    <article className="mb-5">
      <Helmet>
        <title>{t(`PRODUCTS.TITLE`)}</title>
        <body className="landing-page" />
      </Helmet>
      <Container className="video-player-container d-flex p-0 mx-0 w-100">
        <Video
          preload="auto"
          muted // autoPlay only works in Safari when muted
          autoPlay
          disablePictureInPicture
          loop={true}
          playsInline
          nativeControls={false}
          controls
          controlColor="light"
          controlOutline
          controlsList="nodownload nofullscreen noremoteplayback"
          poster={require('./assets/videos/poster_desktop.jpg')}
          containerClassName="d-none d-md-block sticky-top fold-behind"
          className="fullsize-video"
        >
          <source
            src="https://cdn.tickettoaster.de/megaloh/Megaloh_Banner_Desktop_2025_2000x600.mp4"
            type="video/mp4"
          />
        </Video>

        <Video
          preload="auto"
          muted // autoPlay only works in Safari when muted
          autoPlay
          disablePictureInPicture
          loop={true}
          playsInline
          nativeControls={false}
          controls
          controlColor="light"
          controlOutline
          controlsList="nodownload nofullscreen noremoteplayback"
          poster={require('./assets/videos/poster_mobil.jpg')}
          containerClassName="d-block d-md-none sticky-top fold-behind"
          className="fullsize-video"
        >
          {/* <source src={require('./assets/videos/imex-mobile.mp4')} type="video/mp4" /> */}
          <source src="https://cdn.tickettoaster.de/megaloh/Megaloh_Banner_Mobile_2025_800x600.mp4" type="video/mp4" />
        </Video>
      </Container>

      <Container className="list-page-tickets">
        <h1 className="mb-5">TICKETS</h1>

        {!loadingTickets && tickets.length === 0 ? ( // if no tickets
          <Alert color="info">{t(`MASONRY.NO_ITEMS`)}</Alert>
        ) : (
          // else show tickets
          !params.id && ( // but only when not filtered for product category
            <>
              <Masonry id="ticket-listing" products={tickets} type="list" showCity showLocation showSubtitle />
            </>
          )
        )}
      </Container>
    </article>
  );
};

const mapStateToProps = (state: AppState) => {
  const { products } = state.Products;
  const loadingProducts = state.Products.loading;
  const { tickets } = state.Tickets;
  const loadingTickets = state.Tickets.loading;
  return {
    products,
    loadingProducts,
    tickets,
    loadingTickets,
  };
};

const mapDispatchToProps = {
  receiveProducts,
  receiveTickets,
};

export default React.memo(Home);
